import PageHero from '@/components/PageHero';
import { GlassCard } from '@/components/ui/glass-card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Handshake, Users, Building, ArrowRight, Mail } from 'lucide-react';

const Kooperationen = () => {
  const cooperationTypes = [
    {
      title: "Therapeutische Praxen",
      description: "Zusammenarbeit mit Heilpraktikern und ganzheitlichen Therapeuten",
      icon: Building,
      benefits: ["Ergänzende Therapieformen", "Ganzheitlicher Ansatz", "Patientenbetreuung"]
    },
    {
      title: "Wellness-Zentren",
      description: "Kooperationen mit Wellness- und Gesundheitszentren",
      icon: Handshake,
      benefits: ["Erweiterte Angebote", "Neue Zielgruppen", "Synergieeffekte"]
    },
    {
      title: "Bildungseinrichtungen",
      description: "Partnerschaften mit spirituellen und alternativen Bildungsanbietern",
      icon: Users,
      benefits: ["Wissensaustausch", "Gemeinsame Projekte", "Weiterbildung"]
    }
  ];

  return (
    <>
      <PageHero 
        title="Kooperationen" 
        description="Partnerschaften und Zusammenarbeit für ganzheitliche Gesundheit"
        image="https://images.unsplash.com/photo-1521737711867-e3b97375f902"
      />
      
      <section className="py-20 section-bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Introduction */}
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-primary-deep mb-6">
                Unsere Kooperationen
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Wir arbeiten mit verschiedenen Partnern zusammen, um ein umfassendes 
                Netzwerk für ganzheitliche Gesundheit und spirituelle Entwicklung zu schaffen.
              </p>
            </div>

            {/* Cooperation Types */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {cooperationTypes.map((type, index) => (
                <GlassCard key={index} className="p-8 text-center">
                  <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-4">
                    <type.icon className="h-8 w-8 text-primary-deep" />
                  </div>
                  <h3 className="text-xl font-bold text-primary-deep mb-4">
                    {type.title}
                  </h3>
                  <p className="text-gray-700 mb-6">
                    {type.description}
                  </p>
                  <div className="space-y-2">
                    {type.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center justify-center text-gray-600">
                        <span className="w-2 h-2 bg-primary-main rounded-full mr-2"></span>
                        {benefit}
                      </div>
                    ))}
                  </div>
                </GlassCard>
              ))}
            </div>

            {/* Benefits Section */}
            <div className="grid lg:grid-cols-2 gap-8 mb-16">
              <GlassCard className="p-8">
                <h3 className="text-2xl font-bold text-primary-deep mb-6">
                  Vorteile einer Kooperation
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-main rounded-full mr-3 mt-2 shrink-0"></span>
                    Erweiterte Behandlungsmöglichkeiten für Ihre Klienten
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-main rounded-full mr-3 mt-2 shrink-0"></span>
                    Gemeinsame Weiterbildungen und Erfahrungsaustausch
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-main rounded-full mr-3 mt-2 shrink-0"></span>
                    Netzwerk aus ganzheitlich denkenden Praktikern
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-main rounded-full mr-3 mt-2 shrink-0"></span>
                    Empfehlungspartnerschaft für optimale Betreuung
                  </li>
                </ul>
              </GlassCard>

              <GlassCard className="p-8">
                <h3 className="text-2xl font-bold text-primary-deep mb-6">
                  Interesse an einer Kooperation?
                </h3>
                <p className="text-gray-700 mb-6">
                  Wir sind immer auf der Suche nach gleichgesinnten Partnern, die unser 
                  Verständnis von ganzheitlicher Gesundheit und spiritueller Entwicklung teilen.
                </p>
                <div className="space-y-4">
                  <Button asChild size="lg" className="w-full bg-primary-main hover:bg-primary-deep">
                    <a href="mailto:sabrina.kure@yahoo.com">
                      <Mail className="mr-2 h-5 w-5" />
                      Kooperation anfragen
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="w-full border-primary-main text-primary-main hover:bg-primary-main hover:text-white">
                    <Link to="/anmeldung">
                      Mehr erfahren
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </GlassCard>
            </div>

            {/* Call to Action */}
            <GlassCard className="p-8 text-center">
              <h3 className="text-2xl font-bold text-primary-deep mb-4">
                Gemeinsam für ganzheitliche Gesundheit
              </h3>
              <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
                Lassen Sie uns zusammenarbeiten, um Menschen auf ihrem Weg zu 
                Gesundheit, Bewusstsein und spiritueller Entwicklung zu unterstützen.
              </p>
              <Button asChild size="lg" className="bg-primary-main hover:bg-primary-deep">
                <a href="mailto:sabrina.kure@yahoo.com">
                  Partnerschaft beginnen
                </a>
              </Button>
            </GlassCard>
          </div>
        </div>
      </section>
    </>
  );
};

export default Kooperationen;